import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import { ShoppingPartnerCategory } from '../../core/models/api.models';

@Component({
  selector: 'app-shopping-partner-categories',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './shopping-partner-categories.component.html',
  styleUrl: './shopping-partner-categories.component.scss',
})
export class ShoppingPartnerCategoriesComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<ShoppingPartnerCategory[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 50;
  readonly showForm = signal(false);
  readonly editingId = signal<number | null>(null);

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    slug: [''],
    icon_url: [''],
    sort_order: [0],
    is_active: [true],
  });

  ngOnInit(): void {
    this.load();
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    this.api.getShoppingPartnerCategories(page, this.limit).subscribe({
      next: (data) => {
        this.rows.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load categories');
        this.loading.set(false);
      },
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form.reset({
      name: '',
      slug: '',
      icon_url: '',
      sort_order: 0,
      is_active: true,
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  openEdit(row: ShoppingPartnerCategory): void {
    this.editingId.set(row.id);
    this.form.patchValue({
      name: row.name || '',
      slug: row.slug || '',
      icon_url: row.icon_url || '',
      sort_order: Number(row.sort_order || 0),
      is_active: row.is_active !== false,
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const v = this.form.getRawValue();
    const payload = {
      name: v.name.trim(),
      slug: v.slug.trim() || undefined,
      icon_url: v.icon_url.trim() || null,
      sort_order: Number(v.sort_order || 0),
      is_active: !!v.is_active,
    };
    const id = this.editingId();
    const req$ = id
      ? this.api.updateShoppingPartnerCategory(id, payload)
      : this.api.createShoppingPartnerCategory(payload);

    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.editingId.set(null);
        this.success.set(id ? 'Category updated' : 'Category created');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to save category');
      },
    });
  }

  remove(row: ShoppingPartnerCategory): void {
    if (!confirm(`Delete category “${row.name}”?`)) return;
    this.saving.set(true);
    this.api.deleteShoppingPartnerCategory(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Category deleted');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to delete category');
      },
    });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }
}
